import type { ThemeConfig } from 'antd'

export type ColorVars = Record<string, string>
export type ThemeName = 'light' | 'dark'

export const LIGHT_COLORS: ColorVars = {
  '--branco-puro': '#ffffff',
  '--preto-puro': '#000000',
  '--cinza-100': '#f5f8fb',
  '--cinza-300': '#bcbcbc',
  '--cinza-500': '#808080',
  '--cinza-700': '#595959',
  '--cinza-900': '#353535',

  '--cor-primaria': '#262b41',
  '--cor-primaria-hover': '#2d3251',
  '--cor-secundaria': '#e73d3e',
  '--cor-acao-primaria': '#375926',
  '--cor-acao-primaria-hover': '#3d6e30',

  '--cor-sucesso': '#375926',
  '--cor-perigo': 'rgb(94, 27, 27)',
  '--cor-aviso': '#a6481f',
  '--cor-info': 'rgb(31, 62, 87)',
  '--cor-sucesso-trasparente': 'rgba(55, 89, 38, 0.5)',
  '--cor-perigo-trasparente': 'rgba(94, 27, 27, 0.5)',
  '--cor-aviso-trasparente': 'rgba(166, 72, 31, 0.5)',
  '--cor-info-trasparente': 'rgba(31, 62, 87, 0.5)',

  '--azul-puro': '#0059ff',
  '--azul-pastel': '#7693C6',
  '--azul-100': '#afbcd5',
  '--azul-300': '#48afd1',
  '--turquesa-puro': '#53b9d1',
  '--amarelho-puro': '#ffd500',
  '--amarelho-pastel': '#e4cb7b',
  '--amarelo-100': '#e8e4b9',
  '--laranja-puro': '#ff6200',
  '--laranja-pastel': '#CF7537',
  '--laranja-100': '#e8b393',
  '--verde-vivo': '#00ca26',
  '--verde-puro': '#2ca604',
  '--verde-pastel': '#428651',
  '--verde-500': '#529a62',
  '--verde-100': '#abcdb3',
  '--vermelho-puro': '#ff0000',
  '--vermelho-pastel': '#982e2e',
  '--vermelho-300': '#d77172',
  '--vermelho-100': '#d88586',
  '--vermelho-50': '#edc3c3',

  '--cor-texto-primario': '#000000',
  '--cor-texto-secundario': '#595959',
  '--cor-texto-placeholder': '#808080',
  '--cor-texto-inverso': '#bcbcbc',
  '--cor-texto-branco': '#ffffff',
  '--cor-texto-link': 'rgb(31, 62, 87)',
  '--cor-texto-placeholder-input': '#53b9d1',
  '--cor-texto-vermelho': '#982e2e',
  '--cor-texto-laranja': '#CF7537',
  '--cor-texto-verde': '#428651',

  '--cor-fundo-principal': '#f5f8fb',
  '--cor-fundo-secundario': '#ffffff',
  '--cor-fundo-primario-tema': '#262b41',
  '--cor-fundo-secundario-tema': '#e73d3e',
  '--cor-fundo-verde': '#529a62',
  '--cor-fundo-amarelo': '#e4cb7b',
  '--cor-fundo-laranja': '#CF7537',
  '--cor-fundo-vermelho': '#d88586',
  '--cor-fundo-azul': '#7693C6',
  '--cor-fundo-verde-claro': '#abcdb3',
  '--cor-fundo-amarelo-claro': '#e8e4b9',
  '--cor-fundo-laranja-claro': '#e8b393',
  '--cor-fundo-vermelho-claro': '#edc3c3',
  '--cor-fundo-azul-claro': '#afbcd5',

  '--cor-texto-dia-calendario': '#0f172a',
  '--cor-borda-dia-bloqueado': '#d00505',

  '--sombra-padrao': '3px 3px 6px 0px rgba(0, 0, 0, 0.2)',
  '--cor-borda': '#e5e7eb',
  '--cor-scrollbar': '#888',
  '--cor-fundo-hover': '#c8dfec',

  '--tabela-zebra-strength': '18%',
}

export const DARK_COLORS: ColorVars = {
  '--branco-puro': '#ffffff',
  '--preto-puro': '#000000',
  '--cinza-100': '#262833',
  '--cinza-300': '#3a3f4a',
  '--cinza-500': '#596174',
  '--cinza-700': '#7f8899',
  '--cinza-900': '#101218',

  '--cor-primaria': '#2d3251',
  '--cor-primaria-hover': '#404771',
  '--cor-secundaria': '#e73d3e',
  '--cor-acao-primaria': '#4b7a3b',
  '--cor-acao-primaria-hover': '#4e8240',

  '--cor-sucesso': '#4b7a3b',
  '--cor-perigo': '#b34a4a',
  '--cor-aviso': '#c06a2e',
  '--cor-info': '#5a86ad',
  '--cor-sucesso-trasparente': 'rgba(75, 122, 59, 0.5)',
  '--cor-perigo-trasparente': 'rgba(179, 74, 74, 0.5)',
  '--cor-aviso-trasparente': 'rgba(192, 106, 46, 0.5)',
  '--cor-info-trasparente': 'rgba(90, 134, 173, 0.5)',

  '--azul-puro': '#84a9ff',
  '--azul-pastel': '#6f86b6',
  '--turquesa-puro': '#6cc9df',
  '--azul-100': '#8aa0c5',
  '--azul-300': '#48afd1',
  '--amarelho-puro': '#ffd861',
  '--amarelho-pastel': '#e0c46d',
  '--amarelo-100': '#bba86f',
  '--laranja-puro': '#ff7a2a',
  '--laranja-pastel': '#d07b45',
  '--laranja-100': '#a66f4d',
  '--verde-vivo': '#42e36a',
  '--verde-puro': '#33c553',
  '--verde-pastel': '#5fa373',
  '--verde-500': '#67b07d',
  '--verde-100': '#7dbd90',
  '--vermelho-puro': '#ff6464',
  '--vermelho-pastel': '#c96969',
  '--vermelho-300': '#e37f80',
  '--vermelho-100': '#d98f8f',
  '--vermelho-50': '#caa1a1',

  '--cor-texto-primario': '#ffffff',
  '--cor-texto-secundario': '#bcbcbc',
  '--cor-texto-placeholder': '#8a93a6',
  '--cor-texto-inverso': '#0a0a0a',
  '--cor-texto-branco': '#ffffff',
  '--cor-texto-link': '#8ab4ff',
  '--cor-texto-placeholder-input': '#6cc9df',
  '--cor-texto-vermelho': '#e37f80',
  '--cor-texto-laranja': '#d07b45',
  '--cor-texto-verde': '#7ec896',

  '--cor-fundo-principal': '#1a1c23',
  '--cor-fundo-secundario': '#2a2f3a',
  '--cor-fundo-primario-tema': '#2d3251',
  '--cor-fundo-secundario-tema': '#e73d3e',
  '--cor-fundo-verde': '#67b07d',
  '--cor-fundo-amarelo': '#e0c46d',
  '--cor-fundo-laranja': '#d07b45',
  '--cor-fundo-vermelho': '#d98f8f',
  '--cor-fundo-azul': '#6f86b6',
  '--cor-fundo-verde-claro': '#7dbd90',
  '--cor-fundo-amarelo-claro': '#bba86f',
  '--cor-fundo-laranja-claro': '#a66f4d',
  '--cor-fundo-vermelho-claro': '#caa1a1',
  '--cor-fundo-azul-claro': '#8aa0c5',

  '--cor-texto-dia-calendario': '#101218',
  '--cor-borda-dia-bloqueado': '#ff6464',

  '--sombra-padrao': '0 0 0 1px rgba(255,255,255,0.04), 0 8px 24px rgba(0,0,0,.35)',
  '--cor-borda': '#3d4250',
  '--cor-scrollbar': '#666',
  '--cor-fundo-hover': '#2a3a48',

  '--tabela-zebra-strength': '10%',
}

export function applyColorsToRoot(vars: ColorVars) {
  const root = document.documentElement
  Object.entries(vars).forEach(([k, v]) => {
    root.style.setProperty(k, v, 'important')
  })
}

export function tokensFromColors(vars: ColorVars): ThemeConfig['token'] {
  return {
    colorPrimary: vars['--cor-primaria'],
    colorInfo: vars['--cor-info'],
    colorSuccess: vars['--cor-sucesso'],
    colorWarning: vars['--cor-aviso'],
    colorError: vars['--cor-perigo'],
    colorTextBase: vars['--cor-texto-primario'],
    colorBgBase: vars['--cor-fundo-principal'],
    colorBorder: vars['--cor-borda'],
    colorLink: vars['--cor-texto-link'],
    borderRadius: 8,
    fontFamily:
      "Roboto, -apple-system, Segoe UI, Roboto, Helvetica, Arial, 'Noto Sans', 'Apple Color Emoji', 'Segoe UI Emoji', 'Segoe UI Symbol'",
  }
}

export function componentsForTheme(theme: ThemeName) {
  if (theme === 'light') {
    return {
      Table: {
        headerBg: 'var(--cor-fundo-secundario)',
        headerColor: 'var(--cor-texto-primario)',
        rowHoverBg: 'var(--tabela-row-hover-bg)',
        borderColor: 'var(--cor-borda)',
      },
    } as ThemeConfig['components']
  }
  return {} as ThemeConfig['components']
}

export function getThemeColors(theme: ThemeName): ColorVars {
  return theme === 'dark' ? DARK_COLORS : LIGHT_COLORS
}
